import {
  type LlmTraceDrainProps,
  type LlmTraceDrainType,
  createLlmTraceDrain,
  ensureLlmPolicy,
  fetchLlmGatewayConfiguration,
  selectEnvironmentById,
  selectLlmGatewayConfiguration,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { environmentIntegrationsUrl, environmentsUrl } from "@app/routes";
import { handleValidator } from "@app/validator";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useValidator } from "../hooks";
import { EnvironmentDetailLayout } from "../layouts";
import {
  BannerMessages,
  Box,
  ButtonOps,
  EnvironmentSelect,
  FormGroup,
  Input,
  Select,
  type SelectOption,
} from "../shared";

const drainTypeOptions: SelectOption<LlmTraceDrainType>[] = [
  { value: "langfuse", label: "Langfuse" },
];

const validators = {
  handle: (p: LlmTraceDrainProps) => handleValidator(p.handle),
  url: (p: LlmTraceDrainProps) => {
    const trimmed = p.url.trim();
    if (trimmed === "") return "Must provide a base URL";
    try {
      const u = new URL(trimmed);
      if (u.protocol !== "https:") {
        return "URL must use https";
      }
    } catch {
      return "Must be a valid URL";
    }
  },
  publicKey: (p: LlmTraceDrainProps) => {
    if (p.publicKey.trim() === "") return "Must provide a public key";
  },
  secretKey: (p: LlmTraceDrainProps) => {
    if (p.secretKey.trim() === "") return "Must provide a secret key";
  },
};

export const CreateLlmTraceDrainPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const { isLoading: isEnrollmentLoading } = useQuery(
    fetchLlmGatewayConfiguration({ orgId }),
  );
  const gatewayConfig = useSelector(selectLlmGatewayConfiguration);
  const isLlmGatewayEnrolled = !!gatewayConfig?.id;

  const [params] = useSearchParams();
  const queryEnvId = params.get("environment_id") || "";
  const [envId, setEnvId] = useState(queryEnvId);
  const env = useSelector((s) => selectEnvironmentById(s, { id: envId }));

  useEffect(() => {
    if (!envId || !orgId) {
      return;
    }
    dispatch(ensureLlmPolicy({ envId, orgId }));
  }, [dispatch, envId, orgId]);

  const [handle, setHandle] = useState("");
  const [drainType, setDrainType] = useState<LlmTraceDrainType>("langfuse");
  const [url, setUrl] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [secretKey, setSecretKey] = useState("");

  const [errors, validate] = useValidator<
    LlmTraceDrainProps,
    typeof validators
  >(validators);

  const formData = (): LlmTraceDrainProps => ({
    orgId: orgId ?? "",
    accountId: envId,
    handle,
    drainType,
    url,
    publicKey,
    secretKey,
  });

  const loader = useLoader(createLlmTraceDrain);

  const submit = () => {
    if (!envId || !orgId) {
      return;
    }
    const data = formData();
    if (!validate(data)) {
      return;
    }
    dispatch(
      createLlmTraceDrain({
        ...data,
        handle: data.handle.trim(),
        url: data.url.trim(),
        publicKey: data.publicKey.trim(),
        secretKey: data.secretKey.trim(),
      }),
    );
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit();
  };

  const onEnvSelect = (opt: SelectOption) => {
    setEnvId(opt.value);
  };

  const onDrainTypeSelect = (opt: SelectOption<LlmTraceDrainType>) => {
    setDrainType(opt.value);
  };

  useLoaderSuccess(loader, () => {
    navigate(environmentIntegrationsUrl(envId));
  });

  if (isEnrollmentLoading) {
    return (
      <EnvironmentDetailLayout>
        <Box>{null}</Box>
      </EnvironmentDetailLayout>
    );
  }

  if (!isLlmGatewayEnrolled) {
    return (
      <Navigate
        to={envId ? environmentIntegrationsUrl(envId) : environmentsUrl()}
        replace
      />
    );
  }

  return (
    <EnvironmentDetailLayout>
      <Box>
        <h1 className="text-lg text-black font-semibold">
          Create LLM Trace Drain
        </h1>

        <div className="py-4">
          LLM trace drains send LLM gateway trace data from the{" "}
          <strong>{env.handle || "selected"}</strong> environment to an
          observability destination.
        </div>

        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="text-md font-semibold text-gray-900 block -mb-3">
            Environment
          </div>
          <EnvironmentSelect value={envId} onSelect={onEnvSelect} />

          <FormGroup
            label="Handle"
            htmlFor="llm-trace-drain-handle"
            feedbackMessage={errors.handle}
            feedbackVariant={errors.handle ? "danger" : "info"}
          >
            <Input
              type="text"
              id="llm-trace-drain-handle"
              value={handle}
              onChange={(e) => setHandle(e.currentTarget.value)}
              disabled={!envId}
            />
          </FormGroup>

          <FormGroup label="Type" htmlFor="llm-trace-drain-type">
            <Select
              ariaLabel="Type"
              id="llm-trace-drain-type"
              options={drainTypeOptions}
              onSelect={onDrainTypeSelect}
              value={drainType}
            />
          </FormGroup>

          {drainType === "langfuse" ? (
            <>
              <FormGroup
                label="Base URL"
                htmlFor="llm-trace-drain-base-url"
                feedbackMessage={errors.url}
                feedbackVariant={errors.url ? "danger" : "info"}
              >
                <Input
                  type="text"
                  id="llm-trace-drain-base-url"
                  value={url}
                  placeholder="https://cloud.langfuse.com"
                  onChange={(e) => setUrl(e.currentTarget.value)}
                  disabled={!envId}
                  autoComplete="url"
                  inputMode="url"
                />
              </FormGroup>

              <FormGroup
                label="Public Key"
                htmlFor="llm-trace-drain-public-key"
                feedbackMessage={errors.publicKey}
                feedbackVariant={errors.publicKey ? "danger" : "info"}
              >
                <Input
                  type="text"
                  id="llm-trace-drain-public-key"
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.currentTarget.value)}
                  autoComplete="off"
                  disabled={!envId}
                />
              </FormGroup>

              <FormGroup
                label="Secret Key"
                htmlFor="llm-trace-drain-secret-key"
                feedbackMessage={errors.secretKey}
                feedbackVariant={errors.secretKey ? "danger" : "info"}
              >
                <Input
                  type="password"
                  id="llm-trace-drain-secret-key"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.currentTarget.value)}
                  autoComplete="new-password"
                  disabled={!envId}
                />
              </FormGroup>
            </>
          ) : null}

          <BannerMessages {...loader} />

          <ButtonOps
            className="w-[200px]"
            envId={envId}
            type="submit"
            isLoading={loader.isLoading}
            disabled={!envId || !orgId}
          >
            Save LLM Trace Drain
          </ButtonOps>
        </form>
      </Box>
    </EnvironmentDetailLayout>
  );
};
